/**
 * Settings API Service
 * 
 * All settings-related API endpoints following the API v2.0 specification.
 */

import { get, post } from '../../utils/httpClient';
import logger from '../../utils/logger';

/**
 * Get all settings
 */
export const fetchAllSettings = async () => {
  try {
    const response = await get('/settings/list');
    return response.data || [];
  } catch (error) {
    logger.error('Failed to fetch all settings', error);
    throw error;
  }
};

/**
 * Search settings by parameter name
 */
export const searchSettings = async (term) => {
  try {
    if (!term) {
      throw new Error('Search term is required');
    }
    const response = await get(`/settings/search?term=${encodeURIComponent(term)}`);
    return response.data || [];
  } catch (error) {
    logger.error('Failed to search settings', error, { term });
    throw error;
  }
};

/**
 * Get a single setting by ID
 */
export const fetchSettingById = async (id) => {
  try {
    if (!id) {
      throw new Error('Setting ID is required');
    }
    const response = await get(`/settings/view?id=${id}`);
    return response.data;
  } catch (error) {
    logger.error('Failed to fetch setting by ID', error, { id });
    throw error;
  }
};

/**
 * Get a setting by parameter name
 * Helper function that searches and returns the first match
 */
export const fetchSettingByParameter = async (parameter) => {
  try {
    const settings = await searchSettings(parameter);
    const setting = settings.find(s => s.parameter === parameter);
    return setting || null;
  } catch (error) {
    logger.error('Failed to fetch setting by parameter', error, { parameter });
    return null;
  }
};

/**
 * Get coming soon setting value
 */
export const getComingSoonSetting = async () => {
  try {
    const setting = await fetchSettingByParameter('COMING_SOON');
    if (!setting || !setting.enabled) {
      return false;
    }
    return setting.value === 'true';
  } catch (error) {
    logger.error('Failed to get coming soon setting', error);
    return false; // Default to false if error
  }
};

/**
 * Get countdown date setting value
 */
export const getCountdownDateSetting = async () => {
  try {
    const setting = await fetchSettingByParameter('COUNTDOWN_DATE');
    if (!setting || !setting.enabled) {
      return null;
    }
    return setting.value;
  } catch (error) {
    logger.error('Failed to get countdown date setting', error);
    return null;
  }
};

